<template>
  <div class="cta">
    <div v-if="!$store.state.isRoomAvailabilityLoaded" class="price">...</div>
    <div v-else-if="isAvailable" class="price">
      <span v-if="averagePricePreferred === 0" class="rate">
        ${{ Math.round(averagePriceStandardToDisplay) }}
        <span class="less-emphasis">avg/night</span>
      </span>
      <span v-else class="rate">
        <span class="less-emphasis">Standard:</span>
        <span class="strike">${{ Math.round(averagePriceStandardToDisplay) }}</span>
        <span class="less-emphasis">avg/night</span>
        <br/>
        <span class="rate" :data-tooltip="preferredTooltipCopy">
          <span class="less-emphasis">Members:</span> $ {{ Math.round(averagePricePreferredToDisplay) }}
        <span class="less-emphasis">avg/night</span>
      </span>
      </span>

      <template v-if="roomAvailabilityData.ROOM_TYPES.length">
        <template v-if="!showRateDetails">
          <div v-if="addResortFeeToRoomRate" class="less-emphasis rate-subtotal">
            <span class="less-emphasis">
              Room Rate ${{ formatAsDollarAmount(averageSubtotal) }} avg/night
            </span>
          </div>
          <div v-if="addResortFeeToRoomRate" class="less-emphasis rate-resort-fee">
            <span class="less-emphasis">
              Resort Fee ${{ formatAsDollarAmount(averageResortFee) }} /night
            </span>
          </div>
          <div v-else class="less-emphasis rate-resort-fee">
            <span class="less-emphasis">
              Plus Resort Fee of ${{ formatAsDollarAmount(averageResortFee) }} /night
            </span>
          </div>
        </template>
        <table v-else class="hotel_room--cta_rate_table less-emphasis" aria-label="Rate information for selected stay">
          <tr class="less-emphasis rate-subtotal">
            <td>Room Rate</td>
            <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(subtotal, false) }}</td>
            <td v-else>${{ formatAsDollarAmount(averageSubtotal, false) }} avg/night</td>
          </tr>

          <template v-if="!showRateDetails">
            <tr class="less-emphasis rate-resort-fee">
              <td>Resort Fee</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(averageResortFee) }} /night</td>
              <td v-else>${{ formatAsDollarAmount(averageResortFee) }} /night</td>
            </tr>
          </template>

          <template v-if="showRateDetails">
            <tr class="less-emphasis rate-tax">
              <td>Room Tax</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(totalRoomTax, false) }}</td>
              <td v-else>${{ formatAsDollarAmount(averageRoomTax, false) }} avg/night</td>
            </tr>
            <tr class="less-emphasis rate-resort-fee">
              <td>Resort Fee</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(totalResortFee, false) }}</td>
              <td v-else>${{ formatAsDollarAmount(averageResortFee, false) }} /night</td>
            </tr>
            <tr class="less-emphasis rate-resort-fee-tax">
              <td>Resort Fee Tax</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(totalResortFeeTax, false) }}</td>
              <td v-else>${{ formatAsDollarAmount(averageResortFeeTax, false) }} /night</td>
            </tr>
            <tr class="less-emphasis rate-tourism-surcharge">
              <td>Tourism Surcharge</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(totalTourismSurcharge, false) }}</td>
              <td v-else>${{ formatAsDollarAmount(averageTourismSurcharge, false) }} /night</td>
            </tr>
            <tr class="less-emphasis rate-total">
              <td>Total</td>
              <td v-if="showRateDetailTotals">${{ formatAsDollarAmount(total, false) }}</td>
              <td v-else>${{ formatAsDollarAmount(averageTotal, false) }} avg/night</td>
            </tr>
          </template>
        </table>
      </template>

      <a v-if="!showRateDetails" class="info" title="Click to view rate details" href="#" @click.prevent.stop="openRateDialog">
        <svg>
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#symbol_info"></use>
        </svg>
      </a>
    </div>
    <template v-else>
      <div v-if="minStayDays > 0" class="price">
        <p class="quote">Minimum stay length is {{ minStayDays }} night{{ ((1 < minStayDays) ? 's' : '') }}.</p>
        <p class="quote">You have selected a {{ $store.state.stayLength }} night stay.</p>
      </div>
      <div v-else-if="roomAdjacentDates.length" class="price">
        <p class="quote">Selected dates unavailable. Check in on:</p>
        <template v-for="adjacentDate in roomAdjacentDates">
          <a v-if="adjacentDate.hasOwnProperty('AHEAD') && adjacentDate.AHEAD.hasOwnProperty('ADJACENT_DAY_SHIFT')"
             class="quote quote-ahead" href="#"
             @click.prevent="adjacentDateClickHandler( adjacentDate.AHEAD.ADJACENT_DAY_SHIFT)">
            {{ adjacentDate.AHEAD.CHECKIN_DATE }} for {{ getAdjacentDatePrice(adjacentDate.AHEAD) }} avg/night*
          </a>
          <a
            v-else-if="adjacentDate.hasOwnProperty('BEHIND') && adjacentDate.BEHIND.hasOwnProperty('ADJACENT_DAY_SHIFT')"
            class="quote behind" href="#"
            @click.prevent="adjacentDateClickHandler(adjacentDate.BEHIND.ADJACENT_DAY_SHIFT)">
            {{ adjacentDate.BEHIND.CHECKIN_DATE }} for {{ getAdjacentDatePrice(adjacentDate.BEHIND) }} avg/night*
          </a>
        </template>
      </div>
      <div v-else class="price">
        Sold Out

        <div class="less-emphasis rate-resort-fee">
          <span class="less-emphasis">&nbsp;</span>
        </div>
      </div>
    </template>

    <div class="cta--dates">
      <p><span class="less-emphasis">Check In</span> <span v-html="checkInDateFormatted"></span></p>
      <p><span class="less-emphasis">Check Out</span> <span v-html="checkOutDateFormatted"></span></p>
    </div>

    <a v-if="!$store.state.isRoomAvailabilityLoaded" class="btn get-rates btn-disabled" href="#">Loading Rates</a>
    <a v-else-if="isAvailable" class="btn get-rates" :href="bookNowLink"
       @click="getRatesClickHandler">Book Now</a>
    <a v-else-if="minStayDays > 0" class="btn" href="#" @click.prevent="updateCheckOutDateForMinNights">Make
      {{ minStayDays }} Night Stay</a>
    <a v-else class="btn get-rates btn-disabled" href="#">Not Available</a>

    <a v-if="showLearnMoreButton" class="btn btn-dark" :href="page.URL" :title="'Learn more about the ' + page.TITLE">Learn
      More</a>
  </div>
</template>

<script lang="ts">
/*!
 * Copyright (C) 2022-2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-16
 *			- Refactor filter-widget.js hotel room call-to-action related functionality to <hotel-room-call-to-action> component.
 *
 *		2023-02-16 (jhoward)
 *			- Add resort fee mention to <hotel-room-call-to-action>.
 *			- Hide info dialog for resort fee info in <hotel-room-call-to-action>.
 *
 *		2023-03-15 (jhoward)
 *			- Add subtotal amount under price in to <hotel-room-call-to-action>.
 *
 *		2023-03-20 (jhoward)
 *			- Add boolean showRateDetails prop to <hotel-room-call-to-action>.
 *			- Add <hotel-room-call-to-action>::formatAsDollarAmount().
 *			- Round standard/preferred average rates in <hotel-room-call-to-action>::formatAsDollarAmount().
 *
 *		2024-06-10 (jhoward)
 *			- Implement Typescript in <hotel-room-call-to-action>.
 *			- Add showRateDetailTotals prop to <hotel-room-call-to-action> to facilitate displaying totals rather than daily averages.
 *			- Add addResortFeeToRoomRate prop to <hotel-room-call-to-action>.
 *      - Add rate detail dialog to <hotel-room-call-to-action>.
 *
 *		2024-06-19 (jhoward)
 *			- Fix adjacent night selection links in <hotel-room-call-to-action>.
*/

import {mapGetters, mapState} from "vuex";
import {defineComponent, PropType} from "vue";
import {HotelRoomAdjacentDate, HotelRoomAdjacentDateRooms} from "../../store/hotel-rooms";
import {HotelPage, RoomAvailabilityData} from "./defs";

export default defineComponent({
  name: 'hotelRoomCallToAction',
  props: {
    offerCodes: {
      type: Array as PropType<string[]>,
      required: true
    },
    page: {
      type: Object as PropType<HotelPage>,
      required: true
    },
    showLearnMoreButton: {
      type: Boolean,
      default: false
    },
    showRateDetails: {
      type: Boolean,
      default: false
    },
    showRateDetailTotals: {
      type: Boolean,
      default: false
    },
    addResortFeeToRoomRate: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      preferredLoggedIn: false,
      taxRate: 0.13,
      tourismSurchargeRate: 2,
    };
  },
  mounted() {
    $(() => {
      if (window.hasOwnProperty('peppermillPreferred') && window.peppermillPreferred.checkForPeppermillPreferredLoginToken()) {
        //user is logged in, let's check their token
        const tokenData = window.peppermillPreferred.getPeppermillPreferredLoginData();
        if (false !== tokenData) {
          this.preferredLoggedIn = true;
        }
      }
    });
  },
  computed: {
    ...mapState([
      'ROOMS',
      'ADJACENT_DATES',
      'isRoomAvailabilityLoaded',
      'checkInDate',
      'checkOutDate'
    ]),
    ...mapGetters([
      'stayLength',
      'bookingLink'
    ]),

    averagePriceStandardToDisplay() {
      return (this.addResortFeeToRoomRate)
        ? this.averagePriceStandardWithResortFee
        : this.averagePriceStandard;
    },
    averagePricePreferredToDisplay() {
      return (this.addResortFeeToRoomRate)
        ? this.averagePricePreferredWithResortFee
        : this.averagePricePreferred;
    },

    averagePriceStandardWithResortFee() {
      return this.averagePriceStandard + (this.roomAvailabilityData.ROOM_TYPES[0]?.RESORT_FEE_RATE || 0);
    },

    averagePricePreferredWithResortFee() {
      return this.averagePricePreferred + (this.roomAvailabilityData.ROOM_TYPES[0]?.RESORT_FEE_RATE || 0);
    },

    isAvailable(): boolean {
      return this.$store.getters.roomTypeIsAvailable(this.roomAvailabilityData);
    },
    isPreferredRate(): boolean {
      return this.averagePricePreferred > 0;
    },

    roomAvailabilityData(): RoomAvailabilityData {
      if (this.page.ID > 0) {
        for (let i = 0, l = this.$store.state.ROOMS.length; i < l; i++) {
          if (this.$store.state.ROOMS[i].PAGE_ID === this.page.ID) {
            return this.$store.state.ROOMS[i];
          }
        }
      }
      return {
        ADA_VARIANTS: [],
        DATA_AVAILABLE: false,
        PAGE_ID: 0,
        ROOM_TYPES: []
      };
    },

    minStayDays: function () {
      return this.$store.getters.roomTypeMinStayDays(this.roomAvailabilityData);
    },

    roomAdjacentDates: function () {
      return this.$store.state.ADJACENT_DATES.filter((adjacentDate: HotelRoomAdjacentDate) => {
        return adjacentDate.PAGE_ID === this.page.ID;
      });
    },

    averagePriceStandard: function () {
      if (Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].ROOM_AVERAGE_PRICE || 0;
      }

      return 0;
    },
    averagePricePreferred: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].PREFERRED_AVERAGE_PRICE || 0;
      }

      return 0;
    },

    totalPriceStandard: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].ROOM_TOTAL_PRICE || 0;
      }

      return 0;
    },
    totalPricePreferred: function () {
      if (this.roomAvailabilityData.hasOwnProperty('ROOM_TYPES') && Array.isArray(this.roomAvailabilityData.ROOM_TYPES) && this.roomAvailabilityData.ROOM_TYPES.length) {
        return this.roomAvailabilityData.ROOM_TYPES[0].PREFERRED_TOTAL_PRICE || 0;
      }

      return 0;
    },

    preferredTooltipCopy: function () {
      return this.preferredLoggedIn
        ? 'You are logged in and will receive our member rate!'
        : 'Join our free Preferred program for member rates.';
    },
    preferredOverlayCopy: function () {
      return this.preferredLoggedIn
        ? 'You are logged in and will receive our member rate!'
        : 'Join our free Preferred program for member rates. Prices require Peppermill Preferred account (signup for free).';
    },

    averageRoomTax() {
      return this.averageSubtotal * this.taxRate;
    },
    totalRoomTax() {
      return this.subtotal * this.taxRate;
    },

    averageResortFee() {
      return (0 < this.roomAvailabilityData.ROOM_TYPES.length)
        ? this.roomAvailabilityData.ROOM_TYPES[0].RESORT_FEE_RATE
        : 0;
    },
    totalResortFee() {
      return this.averageResortFee * this.$store.getters.stayLength;
    },

    averageResortFeeTax() {
      return this.averageResortFee * this.taxRate;
    },
    totalResortFeeTax() {
      return this.totalResortFee * this.taxRate;
    },

    averageTourismSurcharge() {
      return this.tourismSurchargeRate;
    },
    totalTourismSurcharge() {
      return this.tourismSurchargeRate * this.$store.getters.stayLength;
    },

    averageSubtotal() {
      if (0 < this.totalPricePreferred) {
        return this.averagePricePreferred;
      } else if (0 < this.totalPriceStandard) {
        return this.averagePriceStandard;
      }

      return 0;
    },
    subtotal() {
      return (this.isPreferredRate)
        ? this.totalPricePreferred
        : this.totalPriceStandard;
    },

    averageTotal() {
      return this.averageSubtotal + this.averageRoomTax + this.averageResortFee + this.averageResortFeeTax + this.averageTourismSurcharge;
    },
    total() {
      return this.subtotal + this.totalRoomTax + this.totalResortFee + this.totalResortFeeTax + this.totalTourismSurcharge;
    },

    checkInDateFormatted(): string {
      if (this.$store.state.checkInDate) {
        return window.pepDateFunctions.formatShortDisplayDate(this.$store.state.checkInDate);
      }

      return '...';
    },
    checkOutDateFormatted(): string {
      if (this.$store.state.checkOutDate) {
        return window.pepDateFunctions.formatShortDisplayDate(this.$store.state.checkOutDate);
      }

      return '...';
    },

    bookNowLink(): string {
      return this.$store.getters.bookingLink(this.roomAvailabilityData, this.roomAvailabilityData.ROOM_TYPES[0]?.PRT_ID || 0, this.offerCodes[0] || '', this.page.ID);
    },
  },
  methods: {
    openRateDialog: function () {
      if (this.isPreferredRate) {
        this.openRateDialogPreferred();
      } else {
        this.openRateDialogStandard();
      }
    },
    openRateDialogPreferred: function () {
      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: 'Preferred Rate Information for Your Selected Stay',
        confirmBody: '<p>' + this.preferredOverlayCopy + '</p><p>$' + this.formatAsDollarAmount(this.averagePricePreferredToDisplay) + ' is the average nightly rate for the ' + this.page.TITLE + '. This includes resort fee and average room rate.</p>' + this.getRateTableHtml(),
        // callback: 'GENERIC-DIALOG-CLOSE'
      });
    },
    openRateDialogStandard: function () {
      window.showOverlayDialog({
        alertMode: true,
        confirmAccept: 'CLOSE',
        confirmTitle: 'Rate Information for Your Selected Stay',
        confirmBody: '<p>$' + this.formatAsDollarAmount(this.averagePriceStandardToDisplay) + ' is the average nightly rate for the ' + this.page.TITLE + '. This includes resort fee and average room rate.</p>' + this.getRateTableHtml(),
        // callback: 'GENERIC-DIALOG-CLOSE'
      });
    },

    getRateTableHtml(): string {
      return `<table class="hotel_room--cta_rate_table" aria-label="Rate information for selected stay">
          <tr>
            <td>Check In Date:</td>
            <td>${this.checkInDateFormatted}</td>
          </tr>
          <tr>
            <td>Check Out Date:</td>
            <td>${this.checkOutDateFormatted}</td>
          </tr>
          <tr>
            <td>Room Rate:</td>
            <td>$${this.formatAsDollarAmount(this.subtotal, false)}</td>
          </tr>
          <tr>
            <td>Room Tax:</td>
            <td>$${this.formatAsDollarAmount(this.totalRoomTax, false)}</td>
          </tr>
          <tr>
            <td>Resort Fee:</td>
            <td>$${this.formatAsDollarAmount(this.totalResortFee, false)}</td>
          </tr>
          <tr>
            <td>Resort Fee Tax:</td>
            <td>$${this.formatAsDollarAmount(this.totalResortFeeTax, false)}</td>
          </tr>
          <tr>
            <td>Tourism Surcharge:</td>
            <td>$${this.formatAsDollarAmount(this.totalTourismSurcharge, false)}</td>
          </tr>
          <tr>
            <td>Total:</td>
            <td>$${this.formatAsDollarAmount(this.total, false)}</td>
          </tr>
        </table>`;
    },

    getRatesClickHandler(event: MouseEvent) {
      if ('#' === this.bookNowLink) {
        event.preventDefault();
      }
    },

    updateCheckOutDateForMinNights() {
      if (this.minStayDays > 0 && !this.isAvailable && this.$store.state.checkInDate) {
        this.$store.commit('updateCheckOutDate', window.pepDateFunctions.addDays(this.$store.state.checkInDate, this.minStayDays));
        document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
      }
    },

    adjacentDateClickHandler(daysToShift: number): void {
      const saveCheckOutDate = new Date(this.$store.state.checkOutDate);
      this.$store.commit('updateCheckOutDate', null);
      this.$store.commit('updateCheckInDate', window.pepDateFunctions.addDays(this.$store.state.checkInDate, daysToShift));
      this.$store.commit('updateCheckOutDate', window.pepDateFunctions.addDays(saveCheckOutDate, daysToShift));

      document.querySelector('.page-link-filter .pepvalidate-v3')?.dispatchEvent(new CustomEvent('submit', {detail: 'skipScroll'}));
    },

    getAdjacentDatePrice(data: HotelRoomAdjacentDateRooms): string {
      if (data.hasOwnProperty(0)) {
        if (data[0].hasOwnProperty('PREFERRED_LOWEST_PRICE') && data[0].PREFERRED_LOWEST_PRICE) {
          return '$' + this.formatAsDollarAmount(data[0].PREFERRED_LOWEST_PRICE + this.averageResortFee);
        } else if (data[0].hasOwnProperty('ROOM_LOWEST_PRICE')) {
          return '$' + this.formatAsDollarAmount(data[0].ROOM_LOWEST_PRICE + this.averageResortFee);
        }
      }

      return '';
    },

    formatAsDollarAmount(amount: number, truncate = true): string {
      return '' + window.pepHelperFunctions.formatAsDollarAmount(amount, truncate);
    }
  }
})
</script>

<style scoped>
.cta--dates {
  display: none;
  grid-template-columns: 1fr 1fr;
  text-align: center;
  margin-top: 0.5em;
}

.ui-mobile .cta--dates {
  display: grid;
}

.ui-mobile .hotel-widget .cta--dates {
  display: none;
}

.cta--dates p {
  margin: 0;
}
</style>

<style>
.hotel_room--cta_rate_table {
  width: 100%;
  border-collapse: collapse;
}

.hotel_room--cta_rate_table td {
  padding: 0.25em;
}

.hotel_room--cta_rate_table td:first-child {
  color: #696969;
  width: 50%;
  text-align: right;
}

.hotel_room--cta_rate_table tr:last-child {
  font-weight: bold;
  border-bottom: none;
}
</style>